import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  createFreightTemplateRule,
  updateFreightTemplateRule,
  deleteFreightTemplateRule,
  getFreightTemplateRuleList
} from '../../api/freightTemplateRule';
import { message } from 'antd';

// 初始状态
const initialState = {
  ruleList: [],
  loading: false,
  currentRule: null
};

// 异步action - 获取运费模板的地区规则列表
export const fetchFreightTemplateRuleList = createAsyncThunk(
  'freightTemplateRule/fetchList',
  async (templateUid, { rejectWithValue }) => {
    try {
      const response = await getFreightTemplateRuleList(templateUid);
      if (response.data) {
        // 将JSON字符串转换为数组
        return response.data.map(rule => ({
          ...rule,
          regionCodes: JSON.parse(rule.regionCodes || '[]'),
          regionNames: JSON.parse(rule.regionNames || '[]')
        }));
      }
      return [];
    } catch (error) {
      message.error('获取运费地区规则列表失败');
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// 异步action - 创建运费地区规则
export const addFreightTemplateRule = createAsyncThunk(
  'freightTemplateRule/addRule',
  async (ruleData, { rejectWithValue, dispatch }) => {
    try {
      // 将数组转换为JSON字符串
      const dataToSend = {
        ...ruleData,
        regionCodes: JSON.stringify(ruleData.regionCodes),
        regionNames: JSON.stringify(ruleData.regionNames),
        provinceCodes: JSON.stringify(ruleData.provinceCodes),
        provinceNames: JSON.stringify(ruleData.provinceNames)
      };
      const response = await createFreightTemplateRule(dataToSend);
      message.success('创建运费地区规则成功');
      // 刷新列表
      dispatch(fetchFreightTemplateRuleList(ruleData.templateUid));
      return response.data;
    } catch (error) {
      message.error('创建运费地区规则失败');
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// 异步action - 更新运费地区规则
export const modifyFreightTemplateRule = createAsyncThunk(
  'freightTemplateRule/modifyRule',
  async (ruleData, { rejectWithValue, dispatch }) => {
    try {
      // 将数组转换为JSON字符串
      const dataToSend = {
        ...ruleData,
        regionCodes: JSON.stringify(ruleData.regionCodes),
        regionNames: JSON.stringify(ruleData.regionNames)
      };
      const response = await updateFreightTemplateRule(dataToSend);
      message.success('更新运费地区规则成功');
      // 刷新列表
      dispatch(fetchFreightTemplateRuleList(ruleData.templateUid));
      return response.data;
    } catch (error) {
      message.error('更新运费地区规则失败');
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// 异步action - 删除运费地区规则
export const removeFreightTemplateRule = createAsyncThunk(
  'freightTemplateRule/removeRule',
  async ({ id, templateUid }, { rejectWithValue, dispatch }) => {
    try {
      await deleteFreightTemplateRule(id);
      message.success('删除运费地区规则成功');
      // 刷新列表
      dispatch(fetchFreightTemplateRuleList(templateUid));
      return id;
    } catch (error) {
      message.error('删除运费地区规则失败');
      return rejectWithValue(error.response.data);
    }
  }
);

const freightTemplateRuleSlice = createSlice({
  name: 'freightTemplateRule',
  initialState,
  reducers: {
    setCurrentRule: (state, action) => {
      state.currentRule = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取运费地区规则列表
      .addCase(fetchFreightTemplateRuleList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFreightTemplateRuleList.fulfilled, (state, action) => {
        state.loading = false;
        state.ruleList = action.payload;
      })
      .addCase(fetchFreightTemplateRuleList.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setCurrentRule } = freightTemplateRuleSlice.actions;

export default freightTemplateRuleSlice.reducer;
